import React, { useContext, useEffect, useState } from 'react';
import { IntlContext } from 'react-intl';
import toast, { Toaster } from 'react-hot-toast';
import {
    deleteItem,
    ExerciseCreateUpdate,
    ExerciseFilter,
    ExerciseUpdate,
    filterGroup,
    filterName,
    resetExerciseCreate,
    resetFilters,
    resetItem,
    resetSelectGroup,
    saveEditingValue,
    saveValue,
    selectGroup,
    setContext,
    setItem,
    setItems,
} from '../state/exercise.slice';
import { ExerciseFormData } from '../beans/exercise.form.data';
import Select from 'react-select';
import { reactSelectStyles, reactSelectComponents } from 'src/features/_shared/styles/react-select.styles';
import { ExerciseFormDataUpdate } from '../beans/exercise.form.data.update';
import { muscleGroups, SelectableExerciseGroup } from '../utils/muscleGroups';
import { useStoreSelector } from 'src/_shared/store/hook';
import { Exercise, ExerciseCreateRequestGroupEnum, User } from 'src/api/entities';
import { TranslatorManager } from 'src/_shared/managers/TranslatorManager';
import { ContextEnum } from 'src/_shared/enums/context.enum';
import { ExerciseManager } from 'src/_shared/managers/exercise.manager';
import { FormManager } from 'src/_shared/managers/form.manager';
import { PopupManager } from 'src/_shared/managers/popup.manager';
import { QueueManager, QueueKeyType } from 'src/_shared/managers/queue.manager';
import { store } from 'src/_shared/store/configure';
import { FormHelperUtil } from 'src/_shared/utils/form.helper.util';
import { PopupType } from 'src/features/_shared/components/Popup/popup.item';
import { TextField } from 'src/features/_shared/components/TextField';
import { Button } from 'src/features/_shared/components/Button/button.component';
import { PageTransition } from 'src/features/_shared/components/PageTransition/page.transition.component';
import { EmptyState } from 'src/features/_shared/components/EmptyState/empty.state.component';

export const ExercisesView: React.FC = () => {
    const exerciseManager = ExerciseManager.getInstance();
    const notificationManager = PopupManager.getInstance();
    const queueManager = QueueManager.getInstance();
    const translator = TranslatorManager.getInstance();
    const intl = useContext(IntlContext);
    const getMessageByFieldAndError = FormHelperUtil.getFormFieldErrorTranslator('wizard.general.data', intl as any);

    const user: User = useStoreSelector(state => state.reducer.accessSlice.loggedUser);
    const exerciseFormValues: ExerciseCreateUpdate = useStoreSelector(state => state.reducer.exerciseSlice.exerciseCreate);
    const exerciseFilters: ExerciseFilter = useStoreSelector(state => state.reducer.exerciseSlice.exerciseFilters);
    const exerciseEditFormValues: ExerciseUpdate = useStoreSelector(state => state.reducer.exerciseSlice.exerciseUpdate);
    const exercises: Exercise[] = useStoreSelector(state => state.reducer.exerciseSlice.exercises);
    const item: Exercise = useStoreSelector(state => state.reducer.exerciseSlice.exercise);
    const contextState: ContextEnum = useStoreSelector(state => state.reducer.exerciseSlice.context);

    useEffect(() => {
        const getItems = async () => {
            const value = await exerciseManager.search(user.id!);
            store.dispatch(setItems({ value }));
        };
        store.dispatch(resetExerciseCreate());
        getItems();
    }, []);

    const handleStartEdit = (exercise: Exercise) => {
        store.dispatch(setContext({ value: ContextEnum.UPDATE }));
        store.dispatch(setItem({ value: exercise }));
        editingForm.setValue('nameEdit', exercise.name);
        editingForm.setValue('urlEdit', exercise.url);
    };

    const handleCancelUpdate = () => {
        store.dispatch(setContext({ value: ContextEnum.CREATE }));
        store.dispatch(resetItem());
    };

    const updateExercise = async (exercise: Exercise) => {
        const convertedGroup = Object.values(ExerciseCreateRequestGroupEnum).find(element => element === exerciseEditFormValues.groupEdit);

        await exerciseManager.edit(exercise, {
            ...exercise,
            name: exerciseEditFormValues.nameEdit,
            group: convertedGroup,
            url: exerciseEditFormValues.urlEdit,
        });
        await search();
    };

    const handleSaveUpdate = async (exercise: Exercise) => {
        store.dispatch(setContext({ value: ContextEnum.CREATE }));
        store.dispatch(resetItem());
        await queueManager.queue(QueueKeyType.DEFAULT, updateExercise(exercise));
        toast('Dati esercizio aggiornati con successo!');
    };

    const search = async () => {
        if (exerciseFilters != undefined && exerciseFilters.group !== '') {
            const value = await exerciseManager.search(user.id!, exerciseFilters.name, exerciseFilters.group);
            store.dispatch(setItems({ value }));
        } else {
            toast('Selezionare almeno un gruppo muscolare!');
        }
    };

    const createExercise = async () => {
        const convertedGroup = Object.values(ExerciseCreateRequestGroupEnum).find(element => element === getValues('group'));

        await exerciseManager.create({
            name: getValues('name'),
            group: convertedGroup,
            data: {
                author: {
                    id: 0,
                    name: 'zambrinis',
                },
            },
        });
        await search();
        toast('Esercizio aggiunto con successo');
    };

    const handleSubmit = async () => {
        const isFormValid = await trigger();
        if (isFormValid) {
            await queueManager.queue(QueueKeyType.DEFAULT, createExercise());
            resetField('name');
            resetField('group');
        }
    };

    const handleQueryExerciseName = async (key: any, searchTerm: string) => {
        setExerciseFilterValuesState({ name: searchTerm, group: exerciseFilterValuesState.group });
        store.dispatch(filterName({ searchTerm }));
    };

    const handleOnBlur = (key: any, value: string) => {
        if (contextState === ContextEnum.CREATE) {
            store.dispatch(saveValue({ key, value }));
        } else {
            store.dispatch(saveEditingValue({ key, value }));
        }
    };

    const handleFilterGroup = async (selectedGroup: SelectableExerciseGroup) => {
        setExerciseFilterValuesState({ name: exerciseFilterValuesState.name, group: selectedGroup.value });
        store.dispatch(filterGroup({ selectedGroup }));
    };

    const handleDelete = async (value: string) => {
        store.dispatch(deleteItem({ value }));
        await queueManager.queue(QueueKeyType.DEFAULT, exerciseManager.delete(value));
    };

    const handleClear = async () => {
        resetField('queryName');
        setExerciseFilterValuesState(initialExerciseFilterValuesState);
        store.dispatch(resetFilters());
        const value = await exerciseManager.search(user.id!);
        store.dispatch(setItems({ value }));
    };

    const { errors, register, trigger, getValues, control, setValue, resetField } = FormManager.getInstance().buildFormProxy<ExerciseFormData>(
        ExerciseFormData,
        handleSubmit,
        {
            name: exerciseFormValues.name,
            group: exerciseFormValues.group,
        },
        'onChange',
    );

    const editingForm = FormManager.getInstance().buildFormProxy<ExerciseFormDataUpdate>(
        ExerciseFormDataUpdate,
        handleSaveUpdate,
        {
            nameEdit: exerciseEditFormValues.nameEdit,
            groupEdit: exerciseEditFormValues.groupEdit,
            urlEdit: exerciseEditFormValues.urlEdit,
        },
        'onChange',
    );

    const handleSelectGroup = (selectedGroup: SelectableExerciseGroup) => {
        store.dispatch(selectGroup({ selectedGroup }));
        setExerciseFormValuesState({ name: exerciseFormValuesState.name, group: selectedGroup.value });
        setValue('group', selectedGroup.value);
    };

    const handleSearch = async () => {
        await search();
        store.dispatch(resetSelectGroup());
    };

    const [exerciseFormValuesState, setExerciseFormValuesState] = useState<ExerciseCreateUpdate>({ name: '', group: '' });
    const [exerciseFilterValuesState, setExerciseFilterValuesState] = useState<ExerciseFilter>({ name: '', group: '' });
    const initialExerciseFilterValuesState: ExerciseFilter = {
        group: '',
        name: '',
    };

    const PAGE_SIZE = 10;
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil(exercises.length / PAGE_SIZE));
    const paginatedExercises = exercises.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    useEffect(() => {
        setCurrentPage(1);
    }, [exercises.length]);

    const handleStartCreate = () => {
        notificationManager.show({
            type: PopupType.Info,
            title: 'Nuovo esercizio',
            icon: 'bi-activity',
            children: (
                <div>
                    <div className='d-flex flex-column justify-content-between gap-3 bd-highlight mb-3 ps-4 '>
                        <div>
                            <TextField
                                type='text'
                                placeholder={'Nome esercizio'}
                                id='name'
                                fullWidth
                                {...register('name')}
                                error={errors.name}
                                errorMessage={errors.name && getMessageByFieldAndError('name', errors)}
                                onBlur={(e: React.FocusEvent<HTMLInputElement, Element>) => handleOnBlur(e.target.name, e.target.value)}
                            />
                        </div>
                        <div style={{ textAlign: 'left' }}>
                            <Select
                                styles={reactSelectStyles}
                                components={reactSelectComponents}
                                getValue={getValues('group') === '' ? null : muscleGroups.find(obj => obj.value === getValues('group'))}
                                id='group'
                                {...register('group')}
                                options={muscleGroups.sort((a, b) => b.label.localeCompare(a.label)).reverse()}
                                error={errors.group}
                                errorMessage={errors.group && getMessageByFieldAndError('group', errors)}
                                onChange={(choice: SelectableExerciseGroup) => handleSelectGroup(choice)}
                            />
                        </div>
                    </div>
                </div>
            ),
            ctas: {
                primary: {
                    cta: () => {
                        handleSubmit();
                        notificationManager.hide();
                    },
                },
                close: {
                    cta: () => {
                        resetField('name');
                        resetField('group');
                        store.dispatch(resetExerciseCreate());
                    },
                },
            },
        });
    };

    const groupColors: Record<string, string> = {
        CHEST: '#ff4949',
        BACK: '#D93955',
        QUADRICEPS: '#e06040',
        TRICEPS: '#c74a72',
        GLUTEUS: '#b05ca6',
        SHOULDERS: '#7b68c9',
        HARMSTRINGS: '#4a8fd9',
        BICEPS: '#ff4949',
        ABS: '#D93955',
        CALVES: '#e06040',
        TRAPS: '#c74a72',
        ADDUCTORS: '#b05ca6',
        ABDUCTORS: '#7b68c9',
        NECK: '#4a8fd9',
    };

    const getGroupColor = (group?: string) => groupColors[group ?? ''] ?? '#ff4949';

    const getGroupInitials = (group?: string) => {
        const label = muscleGroups.find(g => g.value === group)?.label ?? group ?? '';
        return label.slice(0, 2).toUpperCase();
    };

    const getLevelBadge = (level?: string) => {
        switch (level) {
            case 'BEGINNER': return { label: 'Principiante', bg: '#e6f9ee', color: '#1a7f42' };
            case 'INTERMEDIATE': return { label: 'Intermedio', bg: '#fff8e6', color: '#b8860b' };
            case 'EXPERT': return { label: 'Esperto', bg: '#fef2f2', color: '#b91c1c' };
            default: return null;
        }
    };

    const getEquipmentLabel = (eq?: string) => {
        const map: Record<string, string> = {
            BANDS: 'Bande', FOAM_ROLL: 'Foam Roll', KETTLEBELLS: 'Kettlebell',
            BODYWEIGHT: 'Corpo libero', MACHINE: 'Macchina', CABLE: 'Cavo',
            MEDICINE_BALL: 'Palla medica', EXERCISE_BALL: 'Fitball',
            DUMBBELL: 'Manubrio', BARBELL: 'Bilanciere', OTHER: 'Altro',
        };
        return map[eq ?? ''] ?? eq;
    };

    const getTypeLabel = (type?: string) => {
        const map: Record<string, string> = {
            CARDIO: 'Cardio', OLYMPIC_WEIGHTLIGHTING: 'Olimpico', PLYOMETRICS: 'Pliometria',
            POWERLIFTING: 'Powerlifting', STRETCHING: 'Stretching', STRENGTH: 'Forza',
            STRONGMAN: 'Strongman', CALISTHENICS: 'Calisthenics',
        };
        return map[type ?? ''] ?? type;
    };

    return (
        <PageTransition className='mt-4 px-3 px-md-4'>
            <Toaster />

            {/* ── Page Header ──────────────────────────────────────────── */}
            <div
                className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 p-4 anim-slide-up'
                style={{
                    background: 'linear-gradient(135deg, #fff 0%, #fafafa 100%)',
                    borderRadius: 12,
                    border: '1px solid rgba(0,0,0,0.06)',
                }}
            >
                <div>
                    <div className='d-flex align-items-center gap-2 mb-1'>
                        <i className='bi bi-activity' style={{ fontSize: '1.4rem', color: '#ff4949' }}></i>
                        <h4 className='mb-0 fw-bold' style={{ color: '#333' }}>Esercizi</h4>
                    </div>
                    <p className='mb-0 mt-1' style={{ color: '#666', fontSize: '0.9rem' }}>
                        {exercises.length > 0 ? (
                            <>
                                <span className='badge rounded-pill me-2' style={{ backgroundColor: '#ff4949', color: '#fff', fontSize: '0.8rem' }}>
                                    {exercises.length}
                                </span>
                                esercizi disponibili
                            </>
                        ) : (
                            'Gestisci il tuo catalogo esercizi'
                        )}
                    </p>
                </div>
                <div className='mt-3 mt-md-0'>
                    <Button
                        identifier='Nuovo esercizio'
                        icon={<i className='bi bi-plus-lg' style={{ fontSize: '1rem', color: 'white' }}></i>}
                        onClick={handleStartCreate}
                        variant='primary'
                    />
                </div>
            </div>

            {/* ── Filter Panel ─────────────────────────────────────────── */}
            <div
                className='mb-4 p-3 anim-slide-up'
                style={{
                    backgroundColor: '#f9f9f9',
                    borderRadius: 10,
                    border: '1px solid rgba(0,0,0,0.06)',
                    borderLeft: '3px solid #ff4949',
                }}
            >
                <div className='d-flex align-items-center gap-2 mb-3'>
                    <i className='bi bi-funnel-fill' style={{ fontSize: '0.95rem', color: '#ff4949' }}></i>
                    <span className='fw-semibold' style={{ color: '#333', fontSize: '0.9rem' }}>Filtri</span>
                </div>
                <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-3'>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <TextField
                            type='text'
                            placeholder={'Cerca per nome...'}
                            {...register('queryName')}
                            id='name'
                            fullWidth
                            onBlur={(e: React.FocusEvent<HTMLInputElement, Element>) => handleQueryExerciseName(e.target.name, e.target.value)}
                        />
                    </div>
                    <div style={{ minWidth: 220 }}>
                        <Select
                            styles={{
                                ...reactSelectStyles,
                                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                            }}
                            components={reactSelectComponents}
                            menuPortalTarget={document.body}
                            value={
                                exerciseFilterValuesState.group === '' || exerciseFilterValuesState.group === undefined
                                    ? null
                                    : muscleGroups.find(obj => obj.value === exerciseFilterValuesState.group)
                            }
                            options={muscleGroups}
                            name={register('queryGroup').name}
                            onChange={(choice: SelectableExerciseGroup) => handleFilterGroup(choice)}
                            placeholder='Gruppo muscolare...'
                        />
                    </div>
                    <div className='d-flex gap-2'>
                        <Button
                            identifier='Cerca'
                            icon={<i className='bi bi-search' style={{ fontSize: '0.9rem', color: 'white' }}></i>}
                            onClick={() => handleSearch()}
                            variant='primary'
                        />
                        <Button
                            identifier='Pulisci'
                            icon={<i className='bi bi-x-circle' style={{ fontSize: '0.9rem' }}></i>}
                            onClick={handleClear}
                            variant='secondary'
                        />
                    </div>
                </div>
            </div>

            {/* ── Exercise List ─────────────────────────────────────────── */}
            {exercises.length > 0 ? (
                <>
                    <div className='d-flex align-items-center gap-2 mb-3 anim-slide-up'>
                        <i className='bi bi-list-ul' style={{ fontSize: '1.1rem', color: '#ff4949' }}></i>
                        <h6 className='mb-0 fw-semibold' style={{ color: '#333' }}>Catalogo</h6>
                        <span className='badge rounded-pill' style={{ backgroundColor: '#f2f2f2', color: '#666', fontSize: '0.75rem' }}>
                            {exercises.length}
                        </span>
                    </div>
                    <div className='d-flex flex-column gap-3 mb-4'>
                        {paginatedExercises.map((exercise: Exercise, index: number) => (
                            <div key={exercise.uuid || index}>
                                {/* ── Normal display ─── */}
                                {exercise.id !== item.id && (
                                    <div
                                        className='app-card anim-stagger'
                                        style={{
                                            '--anim-index': index,
                                            borderLeft: `3px solid ${getGroupColor(exercise.group)}`,
                                            padding: 0,
                                            overflow: 'hidden',
                                        } as React.CSSProperties}
                                    >
                                        <div className='d-flex flex-column flex-lg-row align-items-lg-center justify-content-between p-3'>
                                            {/* ── Left: Icon + Info ─── */}
                                            <div className='d-flex align-items-start gap-3 flex-grow-1'>
                                                <div
                                                    style={{
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius: '50%',
                                                        backgroundColor: getGroupColor(exercise.group),
                                                        color: '#fff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                                    }}
                                                >
                                                    <span style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '0.5px' }}>{getGroupInitials(exercise.group)}</span>
                                                </div>

                                                <div className='d-flex flex-column' style={{ minWidth: 0 }}>
                                                    <div className='d-flex align-items-center gap-2 flex-wrap'>
                                                        <h6 className='mb-0 fw-semibold' style={{ color: '#222', fontSize: '1rem' }}>
                                                            {exercise.name}
                                                        </h6>
                                                        {exercise.level && (() => {
                                                            const badge = getLevelBadge(exercise.level);
                                                            return badge ? (
                                                                <span
                                                                    className='badge rounded-pill'
                                                                    style={{ backgroundColor: badge.bg, color: badge.color, fontSize: '0.7rem', fontWeight: 500 }}
                                                                >
                                                                    {badge.label}
                                                                </span>
                                                            ) : null;
                                                        })()}
                                                    </div>

                                                    <span style={{ color: '#aaa', fontSize: '0.78rem', marginTop: 2 }}>
                                                        <i className='bi bi-tag me-1' style={{ fontSize: '0.7rem' }}></i>
                                                        {muscleGroups.find(g => g.value === exercise.group)?.label ?? exercise.group}
                                                    </span>

                                                    <div className='d-flex flex-wrap gap-2 mt-2'>
                                                        {exercise.type && (
                                                            <span className='badge rounded-pill' style={{ backgroundColor: '#f2f2f2', color: '#333', fontSize: '0.72rem', fontWeight: 500 }}>
                                                                <i className='bi bi-lightning me-1' style={{ fontSize: '0.65rem' }}></i>
                                                                {getTypeLabel(exercise.type)}
                                                            </span>
                                                        )}
                                                        {exercise.equipment && (
                                                            <span className='badge rounded-pill' style={{ backgroundColor: '#f2f2f2', color: '#333', fontSize: '0.72rem', fontWeight: 500 }}>
                                                                <i className='bi bi-wrench me-1' style={{ fontSize: '0.65rem' }}></i>
                                                                {getEquipmentLabel(exercise.equipment)}
                                                            </span>
                                                        )}
                                                        {exercise.url && (
                                                            <span className='badge rounded-pill' style={{ backgroundColor: '#e6f0ff', color: '#2563eb', fontSize: '0.72rem', fontWeight: 500 }}>
                                                                <i className='bi bi-play-circle me-1' style={{ fontSize: '0.65rem' }}></i>
                                                                Video
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* ── Right: Actions ─── */}
                                            <div className='d-flex align-items-center gap-2 mt-3 mt-lg-0 ms-lg-3 flex-shrink-0'>
                                                {exercise.url && (
                                                    <Button
                                                        icon={<i className='bi bi-play-fill' style={{ fontSize: '1rem', color: 'white' }}></i>}
                                                        variant='primary'
                                                        onClick={() => window.open(exercise.url, '_blank')}
                                                    />
                                                )}
                                                <Button
                                                    icon={<i className='bi bi-pencil-fill' style={{ fontSize: '1rem', color: 'white' }}></i>}
                                                    variant='primary'
                                                    onClick={() => handleStartEdit(exercise)}
                                                />
                                                <Button
                                                    icon={<i className='bi bi-trash3-fill' style={{ fontSize: '1rem' }}></i>}
                                                    variant='secondary'
                                                    onClick={() => handleDelete(exercise.uuid!)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ── Inline edit mode ─── */}
                                {exercise.id === item.id && (
                                    <div
                                        className='app-card anim-fade-in'
                                        style={{
                                            borderLeft: '3px solid #ff4949',
                                            padding: 0,
                                            overflow: 'hidden',
                                            backgroundColor: '#fffbfa',
                                        }}
                                    >
                                        <div className='p-3'>
                                            <div className='d-flex align-items-center gap-2 mb-3'>
                                                <i className='bi bi-pencil-square' style={{ fontSize: '0.95rem', color: '#ff4949' }}></i>
                                                <span className='fw-semibold' style={{ color: '#333', fontSize: '0.9rem' }}>Modifica esercizio</span>
                                            </div>
                                            <div className='d-flex flex-column flex-md-row gap-3 align-items-stretch align-items-md-end'>
                                                <div style={{ flex: 2, minWidth: 0 }}>
                                                    <label style={{ fontSize: '0.78rem', color: '#888', marginBottom: 4, display: 'block' }}>Nome</label>
                                                    <TextField
                                                        type='text'
                                                        placeholder={'Nome esercizio'}
                                                        id='nameEdit'
                                                        fullWidth
                                                        defaultValue={exerciseEditFormValues.nameEdit}
                                                        {...register('nameEdit')}
                                                        error={editingForm.errors.nameEdit}
                                                        errorMessage={editingForm.errors.nameEdit && getMessageByFieldAndError('nameEdit', editingForm.errors)}
                                                        onBlur={(e: React.FocusEvent<HTMLInputElement, Element>) => {
                                                            handleOnBlur(e.target.name, e.target.value);
                                                        }}
                                                    />
                                                </div>
                                                <div style={{ minWidth: 200 }}>
                                                    <label style={{ fontSize: '0.78rem', color: '#888', marginBottom: 4, display: 'block' }}>Gruppo</label>
                                                    <Select
                                                        styles={reactSelectStyles}
                                                        components={reactSelectComponents}
                                                        value={muscleGroups.find(obj => obj.value === exerciseEditFormValues.groupEdit)}
                                                        id='groupEdit'
                                                        {...register('groupEdit')}
                                                        options={muscleGroups}
                                                        onChange={(choice: SelectableExerciseGroup) => {
                                                            handleSelectGroup(choice);
                                                        }}
                                                    />
                                                </div>
                                                <div style={{ flex: 2, minWidth: 0 }}>
                                                    <label style={{ fontSize: '0.78rem', color: '#888', marginBottom: 4, display: 'block' }}>URL Video</label>
                                                    <TextField
                                                        type='text'
                                                        placeholder={'URL video'}
                                                        id='urlEdit'
                                                        fullWidth
                                                        defaultValue={exerciseEditFormValues.urlEdit}
                                                        {...register('urlEdit')}
                                                        error={editingForm.errors.urlEdit}
                                                        errorMessage={editingForm.errors.urlEdit && getMessageByFieldAndError('urlEdit', editingForm.errors)}
                                                        onBlur={(e: React.FocusEvent<HTMLInputElement, Element>) => {
                                                            handleOnBlur(e.target.name, e.target.value);
                                                        }}
                                                    />
                                                </div>
                                                <div className='d-flex gap-2'>
                                                    <Button
                                                        icon={<i className='bi bi-check-lg' style={{ fontSize: '1rem', color: 'white' }}></i>}
                                                        identifier='Salva'
                                                        onClick={() => handleSaveUpdate(exercise)}
                                                        variant='primary'
                                                    />
                                                    <Button
                                                        icon={<i className='bi bi-x-lg' style={{ fontSize: '1rem' }}></i>}
                                                        identifier='Annulla'
                                                        onClick={() => handleCancelUpdate()}
                                                        variant='secondary'
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* ── Pagination ─────────────────────────────────────── */}
                    {totalPages > 1 && (
                        <div className='d-flex justify-content-between align-items-center mb-4 px-1'>
                            <span style={{ color: '#888', fontSize: '0.82rem' }}>
                                {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, exercises.length)} di {exercises.length}
                            </span>
                            <div className='d-flex align-items-center gap-1'>
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    style={{
                                        border: 'none',
                                        background: 'none',
                                        padding: '6px 8px',
                                        borderRadius: 6,
                                        color: currentPage === 1 ? '#ccc' : '#666',
                                        cursor: currentPage === 1 ? 'default' : 'pointer',
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    <i className='bi bi-chevron-double-left'></i>
                                </button>
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    style={{
                                        border: 'none',
                                        background: 'none',
                                        padding: '6px 8px',
                                        borderRadius: 6,
                                        color: currentPage === 1 ? '#ccc' : '#666',
                                        cursor: currentPage === 1 ? 'default' : 'pointer',
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    <i className='bi bi-chevron-left'></i>
                                </button>

                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                                    .reduce<(number | string)[]>((acc, page, idx, arr) => {
                                        if (idx > 0 && page - (arr[idx - 1] as number) > 1) acc.push('...');
                                        acc.push(page);
                                        return acc;
                                    }, [])
                                    .map((page, idx) =>
                                        typeof page === 'string' ? (
                                            <span key={`dots-${idx}`} style={{ padding: '6px 4px', color: '#aaa', fontSize: '0.82rem' }}>...</span>
                                        ) : (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                style={{
                                                    border: 'none',
                                                    padding: '6px 11px',
                                                    borderRadius: 6,
                                                    fontSize: '0.82rem',
                                                    fontWeight: page === currentPage ? 600 : 400,
                                                    backgroundColor: page === currentPage ? '#ff4949' : 'transparent',
                                                    color: page === currentPage ? '#fff' : '#666',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s ease',
                                                }}
                                            >
                                                {page}
                                            </button>
                                        )
                                    )}

                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    style={{
                                        border: 'none',
                                        background: 'none',
                                        padding: '6px 8px',
                                        borderRadius: 6,
                                        color: currentPage === totalPages ? '#ccc' : '#666',
                                        cursor: currentPage === totalPages ? 'default' : 'pointer',
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    <i className='bi bi-chevron-right'></i>
                                </button>
                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    style={{
                                        border: 'none',
                                        background: 'none',
                                        padding: '6px 8px',
                                        borderRadius: 6,
                                        color: currentPage === totalPages ? '#ccc' : '#666',
                                        cursor: currentPage === totalPages ? 'default' : 'pointer',
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    <i className='bi bi-chevron-double-right'></i>
                                </button>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <EmptyState
                    icon='bi bi-activity'
                    title='Nessun esercizio'
                    description='Non ci sono ancora esercizi nel catalogo. Aggiungi il primo esercizio per iniziare.'
                    action={<Button identifier='Nuovo esercizio' onClick={handleStartCreate} variant='primary' />}
                />
            )}
        </PageTransition>
    );
};
